import React, { useState, useEffect, useCallback } from 'react';
import { Sidebar } from '../includes/sidebar';
import { Topbar } from '../includes/topbar';
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from '../../components/ui/breadcrumb';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';
import {
    Search,
    Filter,
    CheckCircle2,
    XCircle,
    Clock,
    Eye,
    MessageSquare,
    Loader2,
    CreditCard,
    User,
    FileText,
    MapPin,
    DollarSign,
    AlertCircle,
    ChevronLeft,
    ChevronRight,
    RefreshCw,
    Image as ImageIcon,
    ExternalLink,
    Send,
    Info,
    History,
} from 'lucide-react';
import { cardAdminApi, CardApplication, ApplicationStatus, CardTier, ApplicationTransaction } from '../../api/cards.api';
import { useToast } from '../../hooks/use-toast';
import { cn } from '../../lib/utils';

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatUSD = (v?: number) =>
    v != null ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v) : '—';

const formatDate = (d?: string) =>
    d ? new Date(d).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

const STATUS_LABEL: Record<string, string> = {
    draft: 'Brouyon',
    submitted: 'Soumèt',
    pending_review: 'An Revizyon',
    approved: 'Apwouve',
    rejected: 'Refize',
    provider_rejected: 'Founisè rejte',
    sent_to_provider: 'Voye bay founisè',
    provider_pending: 'Founisè ap trete',
    awaiting_activation: 'Prèt pou aktivasyon',
    activating: 'Ap aktive',
    card_issued: 'Kat delivre',
};

const STATUS_COLOR: Record<string, string> = {
    draft: 'bg-slate-50 text-slate-500 border-slate-200',
    submitted: 'bg-blue-50 text-blue-600 border-blue-200',
    pending_review: 'bg-amber-50 text-amber-600 border-amber-200',
    approved: 'bg-green-50 text-green-600 border-green-200',
    rejected: 'bg-red-50 text-red-600 border-red-200',
    provider_rejected: 'bg-rose-50 text-rose-700 border-rose-200',
    sent_to_provider: 'bg-indigo-50 text-indigo-600 border-indigo-200',
    provider_pending: 'bg-violet-50 text-violet-600 border-violet-200',
    awaiting_activation: 'bg-orange-50 text-orange-600 border-orange-200',
    activating: 'bg-cyan-50 text-cyan-600 border-cyan-200',
    card_issued: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const TIER_COLOR: Record<CardTier, string> = {
    standard: 'bg-slate-100 text-slate-700 border-slate-300',
    premium: 'bg-amber-50 text-amber-700 border-amber-300',
};

const STATUS_FILTERS: { key: ApplicationStatus | 'all'; label: string }[] = [
    { key: 'all', label: 'Tout' },
    { key: 'pending_review', label: 'An Revizyon' },
    { key: 'submitted', label: 'Soumèt' },
    { key: 'approved', label: 'Apwouve' },
    { key: 'rejected', label: 'Refize' },
    { key: 'provider_rejected', label: 'Founisè rejte' },
    { key: 'card_issued', label: 'Kat delivre' },
];

// ─── Sub-components ──────────────────────────────────────────────────────────

const StatusBadge = ({ status }: { status: string }) => (
    <Badge variant="outline" className={cn('text-xs font-semibold', STATUS_COLOR[status] ?? 'bg-slate-50 text-slate-500')}>
        {STATUS_LABEL[status] ?? status}
    </Badge>
);

const TierBadge = ({ tier }: { tier: CardTier }) => (
    <Badge variant="outline" className={cn('text-xs font-medium capitalize', TIER_COLOR[tier])}>
        {tier === 'premium' ? '★ Premium' : 'Standard'}
    </Badge>
);

// KYC Document image viewer with loading + error states
const DocImage = ({ label, r2Key }: { label: string; r2Key?: string }) => {
    const [url, setUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    useEffect(() => {
        if (!r2Key) return;
        setLoading(true);
        setError(false);
        cardAdminApi
            .getDocumentUrl(r2Key)
            .then((res) => setUrl(res.url))
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    }, [r2Key]);

    return (
        <div className="flex flex-col gap-1.5">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</p>
            <div className="w-full h-36 rounded-xl border border-slate-200 bg-slate-50 overflow-hidden flex items-center justify-center relative">
                {!r2Key ? (
                    <div className="flex flex-col items-center gap-2 text-slate-300">
                        <ImageIcon className="h-8 w-8" />
                        <span className="text-xs">Pa disponib</span>
                    </div>
                ) : loading ? (
                    <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                ) : error ? (
                    <div className="flex flex-col items-center gap-2 text-slate-300">
                        <AlertCircle className="h-6 w-6" />
                        <span className="text-xs text-center px-2">Imaj pa disponib<br/>(efase apre soumisyon)</span>
                    </div>
                ) : url ? (
                    <>
                        <img
                            src={url}
                            alt={label}
                            className="w-full h-full object-cover"
                            onError={() => { setUrl(null); setError(true); }}
                        />
                        <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="absolute bottom-2 right-2 bg-white/90 rounded-lg p-1 shadow hover:bg-white"
                        >
                            <ExternalLink className="h-3.5 w-3.5 text-slate-600" />
                        </a>
                    </>
                ) : null}
            </div>
        </div>
    );
};

const TX_TYPE_LABEL: Record<string, string> = {
    card_apply_fee: 'Frè aplikasyon kat',
    card_first_deposit: '1ᵉʳ depò kat',
    card_recharge: 'Rechaj kat',
    refund: 'Ranbousman',
    hold: 'Blokaj fon',
    release: 'Liberasyon fon',
    debit: 'Debi',
    credit: 'Kredi',
};

const TX_STATUS_COLOR: Record<string, string> = {
    pending: 'bg-amber-50 text-amber-600 border-amber-100',
    confirmed: 'bg-green-50 text-green-600 border-green-200',
    completed: 'bg-green-50 text-green-600 border-green-200',
    refunded: 'bg-blue-50 text-blue-600 border-blue-100',
    failed: 'bg-red-50 text-red-500 border-red-100',
    cancelled: 'bg-slate-50 text-slate-500 border-slate-200',
};

// ─── Main Component ──────────────────────────────────────────────────────────

type ActionType = 'approve' | 'reject' | 'request_info';

export default function ApplicationsPage() {
    const { toast } = useToast();

    // List state
    const [applications, setApplications] = useState<CardApplication[]>([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState<ApplicationStatus | 'all'>('pending_review');
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [pendingCount, setPendingCount] = useState(0);
    const LIMIT = 20;

    // Detail modal state
    const [selected, setSelected] = useState<CardApplication | null>(null);
    const [docTab, setDocTab] = useState<'kyc' | 'details' | 'transactions'>('kyc');
    const [transactions, setTransactions] = useState<ApplicationTransaction[]>([]);
    const [txLoading, setTxLoading] = useState(false);

    // Action state
    const [actionType, setActionType] = useState<ActionType | null>(null);
    const [actionNote, setActionNote] = useState('');
    const [actionLoading, setActionLoading] = useState(false);
    const [retryAppId, setRetryAppId] = useState<string | null>(null);

    // ── Data loading ──────────────────────────────────────────────────────────

    const loadApplications = useCallback(async () => {
        try {
            setLoading(true);
            const res = await cardAdminApi.listApplications({
                page,
                limit: LIMIT,
                status: statusFilter,
                search: searchTerm || undefined,
            });
            setApplications(Array.isArray(res?.results) ? res.results : []);
            setTotal(res?.total ?? 0);
            if (res?.pendingCount != null) setPendingCount(res.pendingCount);
        } catch (err) {
            console.error(err);
            setApplications([]);
            toast({ title: 'Erè', description: 'Pa kapab chaje aplikasyon yo', type: 'error' });
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, statusFilter, searchTerm]);

    useEffect(() => {
        loadApplications();
    }, [loadApplications]);

    // Reset page on filter/search change
    useEffect(() => {
        setPage(1);
    }, [statusFilter, searchTerm]);

    const openDetail = async (app: CardApplication) => {
        setSelected(app);
        setDocTab('kyc');
        setActionType(null);
        setActionNote('');
        setTransactions([]);
        setTxLoading(true);
        try {
            const txs = await cardAdminApi.getApplicationTransactions(app.id);
            setTransactions(txs);
        } catch {
            setTransactions([]);
        } finally {
            setTxLoading(false);
        }
    };

    // ── Actions ───────────────────────────────────────────────────────────────

    const handleAction = async () => {
        if (!selected || !actionType) return;
        if ((actionType === 'reject' || actionType === 'request_info') && !actionNote.trim()) {
            toast({ title: 'Atansyon', description: 'Ou dwe ekri yon rezon / nòt', type: 'error' });
            return;
        }
        try {
            setActionLoading(true);
            if (actionType === 'approve') {
                await cardAdminApi.approveApplication(selected.id, actionNote || undefined);
                toast({ title: 'Siksè ✓', description: 'Aplikasyon apwouve epi voye bay HyperPay', type: 'success' });
            } else if (actionType === 'reject') {
                await cardAdminApi.rejectApplication(selected.id, actionNote);
                toast({ title: 'Refize', description: 'Aplikasyon refize avèk siksè', type: 'success' });
            } else {
                await cardAdminApi.requestMoreInfo(selected.id, actionNote);
                toast({ title: 'Nòt voye', description: 'Demand plis enfòmasyon voye bay kliyan', type: 'success' });
            }
            setSelected(null);
            setActionType(null);
            setActionNote('');
            loadApplications();
        } catch (err: any) {
            toast({
                title: 'Erè',
                description: err.response?.data?.error || 'Yon erè rive pandan tretman an',
                type: 'error',
            });
        } finally {
            setActionLoading(false);
        }
    };

    // Retry HyperPay action is handled via custom Dialog now

    // ── Pagination helpers ────────────────────────────────────────────────────

    const totalPages = Math.max(1, Math.ceil(total / LIMIT));

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <div className="flex h-screen overflow-hidden">
            <Sidebar />
            <div className="flex-1 flex flex-col overflow-hidden">
                <Topbar />
                <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 space-y-6">

                    {/* Breadcrumb */}
                    <Breadcrumb>
                        <BreadcrumbList>
                            <BreadcrumbItem><BreadcrumbLink>Kat VISA</BreadcrumbLink></BreadcrumbItem>
                            <BreadcrumbSeparator />
                            <BreadcrumbItem><BreadcrumbPage>Aplikasyon</BreadcrumbPage></BreadcrumbItem>
                        </BreadcrumbList>
                    </Breadcrumb>

                    {/* Header */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Aplikasyon Kat VISA</h1>
                            <p className="text-slate-500 text-sm mt-1">
                                Revize dokiman KYC, apwouve oswa refize aplikasyon kliyan yo.
                            </p>
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            className="gap-2 border-slate-200 self-start sm:self-auto"
                            onClick={loadApplications}
                        >
                            <RefreshCw className="h-4 w-4" />
                            Aktyalize
                        </Button>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        <Card className="border-none shadow-sm bg-gradient-to-br from-primary-600 to-primary-700 text-white">
                            <CardContent className="pt-5 pb-4">
                                <p className="text-primary-100 text-xs font-semibold uppercase">Total</p>
                                <p className="text-3xl font-bold mt-1">{total}</p>
                            </CardContent>
                        </Card>
                        <Card className="border-none shadow-sm bg-gradient-to-br from-amber-500 to-amber-600 text-white">
                            <CardContent className="pt-5 pb-4">
                                <p className="text-amber-100 text-xs font-semibold uppercase">An Revizyon</p>
                                <p className="text-3xl font-bold mt-1">{pendingCount}</p>
                            </CardContent>
                        </Card>
                        <Card className="border-none shadow-sm bg-white border border-slate-100">
                            <CardContent className="pt-5 pb-4">
                                <p className="text-slate-400 text-xs font-semibold uppercase">Page</p>
                                <p className="text-3xl font-bold mt-1 text-slate-800">{page} / {totalPages}</p>
                            </CardContent>
                        </Card>
                        <Card className="border-none shadow-sm bg-white border border-slate-100">
                            <CardContent className="pt-5 pb-4">
                                <p className="text-slate-400 text-xs font-semibold uppercase">Filtè</p>
                                <p className="text-sm font-bold mt-2 text-slate-700">{STATUS_LABEL[statusFilter] ?? 'Tout'}</p>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Status Filter Tabs */}
                    <div className="flex items-center gap-1 bg-white p-1 rounded-xl border border-slate-200 overflow-x-auto">
                        {STATUS_FILTERS.map(({ key, label }) => (
                            <button
                                key={key}
                                onClick={() => setStatusFilter(key as ApplicationStatus | 'all')}
                                className={cn(
                                    'flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap',
                                    statusFilter === key
                                        ? 'bg-primary-600 text-white shadow-md'
                                        : 'text-slate-600 hover:bg-slate-50'
                                )}
                            >
                                {label}
                            </button>
                        ))}
                    </div>

                    {/* Search */}
                    <div className="flex items-center gap-3">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                            <Input
                                placeholder="Chèche pa non, imèl, oswa ID kliyan..."
                                className="pl-10 h-10 border-slate-200 rounded-xl"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <Button variant="outline" className="gap-2 h-10 px-4 rounded-xl border-slate-200">
                            <Filter className="h-4 w-4" />
                            Filtè
                        </Button>
                    </div>

                    {/* Table */}
                    <Card className="border-slate-200 shadow-sm overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-slate-50/50 border-b border-slate-200">
                                        <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Kliyan</th>
                                        <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Tier</th>
                                        <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Montan</th>
                                        <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Estati</th>
                                        <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Dat</th>
                                        <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500 text-right">Aksyon</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 bg-white">
                                    {loading ? (
                                        <tr>
                                            <td colSpan={6} className="px-6 py-16 text-center">
                                                <Loader2 className="h-8 w-8 animate-spin text-primary-500 mx-auto" />
                                                <p className="text-slate-500 mt-3 text-sm">Ap chaje aplikasyon yo...</p>
                                            </td>
                                        </tr>
                                    ) : applications.length === 0 ? (
                                        <tr>
                                            <td colSpan={6} className="px-6 py-16 text-center">
                                                <div className="bg-slate-50 rounded-full h-14 w-14 flex items-center justify-center mx-auto">
                                                    <CreditCard className="h-7 w-7 text-slate-300" />
                                                </div>
                                                <p className="text-slate-500 mt-3 font-medium">Okenn aplikasyon jwenn</p>
                                                <p className="text-slate-400 text-sm mt-1">Chanje filtè oswa tèm rechèch la</p>
                                            </td>
                                        </tr>
                                    ) : (
                                        applications.map((app) => (
                                            <tr key={app.id} className="hover:bg-slate-50/50 transition-colors cursor-pointer" onClick={() => openDetail(app)}>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className="h-9 w-9 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                                                            <User className="h-4 w-4 text-primary-600" />
                                                        </div>
                                                        <div>
                                                            <p className="text-sm font-bold text-slate-900 leading-tight">
                                                                {app.applicant?.fullName ?? 'Kliyan'}
                                                            </p>
                                                            <p className="text-xs text-slate-400 mt-0.5 font-mono">
                                                                {app.applicant?.email ?? app.clientId.slice(0, 16) + '…'}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <TierBadge tier={app.cardTierSlug} />
                                                </td>
                                                <td className="px-6 py-4">
                                                    <p className="text-sm font-bold text-slate-900">
                                                        {app.amountPaidUsdt != null ? formatUSD(Number(app.amountPaidUsdt)) : '—'}
                                                    </p>
                                                    {app.amountPaidOriginal != null && (
                                                        <p className="text-xs text-slate-400">
                                                            {Number(app.amountPaidOriginal).toLocaleString()} {app.paymentCurrency}
                                                        </p>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <StatusBadge status={app.status} />
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-1.5 text-xs text-slate-400">
                                                        <Clock className="h-3.5 w-3.5" />
                                                        {formatDate(app.submittedAt ?? app.createdAt)}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                                                    {app.status === 'pending_review' || app.status === 'submitted' ? (
                                                        <div className="flex items-center justify-end gap-2">
                                                            <Button
                                                                size="sm"
                                                                className="bg-green-600 hover:bg-green-700 h-8 gap-1.5 shadow-sm"
                                                                onClick={() => { openDetail(app); setTimeout(() => setActionType('approve'), 300); }}
                                                            >
                                                                <CheckCircle2 className="h-3.5 w-3.5" />
                                                                Apwouve
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                className="text-red-600 border-red-100 hover:bg-red-50 h-8 gap-1.5"
                                                                onClick={() => { openDetail(app); setTimeout(() => setActionType('reject'), 300); }}
                                                            >
                                                                <XCircle className="h-3.5 w-3.5" />
                                                                Refize
                                                            </Button>
                                                        </div>
                                                    ) : app.status === 'provider_rejected' ? (
                                                        <div className="flex items-center justify-end gap-2">
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                className="text-orange-600 border-orange-200 hover:bg-orange-50 h-8 gap-1.5"
                                                                onClick={() => setRetryAppId(app.id)}
                                                            >
                                                                <RefreshCw className="h-3.5 w-3.5" />
                                                                Retry
                                                            </Button>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="h-8 w-8 p-0"
                                                                onClick={() => openDetail(app)}
                                                            >
                                                                <Eye className="h-4 w-4 text-slate-400" />
                                                            </Button>
                                                        </div>
                                                    ) : (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="h-8 w-8 p-0"
                                                            onClick={() => openDetail(app)}
                                                        >
                                                            <Eye className="h-4 w-4 text-slate-400" />
                                                        </Button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 bg-slate-50/50">
                                <p className="text-sm text-slate-500">
                                    {(page - 1) * LIMIT + 1}–{Math.min(page * LIMIT, total)} sou {total} aplikasyon
                                </p>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="h-8 w-8 p-0 border-slate-200"
                                        disabled={page <= 1}
                                        onClick={() => setPage((p) => p - 1)}
                                    >
                                        <ChevronLeft className="h-4 w-4" />
                                    </Button>
                                    <span className="text-sm font-medium text-slate-700">{page}</span>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="h-8 w-8 p-0 border-slate-200"
                                        disabled={page >= totalPages}
                                        onClick={() => setPage((p) => p + 1)}
                                    >
                                        <ChevronRight className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        )}
                    </Card>
                </div>
            </div>

            {/* ── Detail Modal ─────────────────────────────────────────────────────── */}
            <Dialog
                open={!!selected && !actionType}
                onOpenChange={(open) => { if (!open) { setSelected(null); setDocTab('kyc'); } }}
            >
                <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col p-0 gap-0">
                    {selected && (
                        <>
                            {/* Modal Header */}
                            <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-100 flex-shrink-0">
                                <div className="flex items-start justify-between gap-4">
                                    <div>
                                        <DialogTitle className="text-lg font-bold text-slate-900 flex items-center gap-2">
                                            <User className="h-5 w-5 text-primary-500" />
                                            {selected.applicant?.fullName ?? 'Kliyan'}
                                        </DialogTitle>
                                        <DialogDescription className="mt-1 flex items-center gap-3">
                                            <StatusBadge status={selected.status} />
                                            <TierBadge tier={selected.cardTierSlug} />
                                        </DialogDescription>
                                    </div>
                                    {(selected.status === 'pending_review' || selected.status === 'submitted') && (
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            <Button
                                                size="sm"
                                                className="bg-green-600 hover:bg-green-700 gap-1.5 shadow-sm"
                                                onClick={() => setActionType('approve')}
                                            >
                                                <CheckCircle2 className="h-3.5 w-3.5" />
                                                Apwouve
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="text-amber-600 border-amber-200 hover:bg-amber-50 gap-1.5"
                                                onClick={() => setActionType('request_info')}
                                            >
                                                <Info className="h-3.5 w-3.5" />
                                                Mande info
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="text-red-600 border-red-100 hover:bg-red-50 gap-1.5"
                                                onClick={() => setActionType('reject')}
                                            >
                                                <XCircle className="h-3.5 w-3.5" />
                                                Refize
                                            </Button>
                                        </div>
                                    )}
                                    {selected.status === 'provider_rejected' && (
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            <Button
                                                size="sm"
                                                className="bg-orange-500 hover:bg-orange-600 gap-1.5 shadow-sm"
                                                onClick={() => setRetryAppId(selected.id)}
                                            >
                                                <RefreshCw className="h-3.5 w-3.5" />
                                                Retry HyperPay
                                            </Button>
                                        </div>
                                    )}
                                </div>

                                {/* Provider Error Banner */}
                                {selected.status === 'provider_rejected' && selected.providerError && (
                                    <div className="mt-3 p-3 bg-rose-50 border border-rose-200 rounded-lg">
                                        <p className="text-sm font-semibold text-rose-700 flex items-center gap-2">
                                            <AlertCircle className="h-4 w-4" />
                                            Refize pa HyperPay
                                        </p>
                                        <p className="text-xs text-rose-600 mt-1">{selected.providerError}</p>
                                        <p className="text-xs text-slate-500 mt-1">
                                            Kliyan resevwa refund + notifikasyon. Li ka korije epi resoumet depi app mobile.
                                        </p>
                                    </div>
                                )}

                                {/* Tabs */}
                                <div className="flex items-center gap-1 mt-4 bg-slate-50 p-1 rounded-lg w-fit">
                                    {([
                                        { key: 'kyc', label: 'Dokiman KYC', icon: <FileText className="h-3.5 w-3.5" /> },
                                        { key: 'details', label: 'Detay', icon: <CreditCard className="h-3.5 w-3.5" /> },
                                        { key: 'transactions', label: 'Tranzaksyon', icon: <DollarSign className="h-3.5 w-3.5" /> },
                                    ] as const).map(({ key, label, icon }) => (
                                        <button
                                            key={key}
                                            onClick={() => setDocTab(key)}
                                            className={cn(
                                                'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all',
                                                docTab === key ? 'bg-white shadow text-primary-700' : 'text-slate-500 hover:text-slate-700'
                                            )}
                                        >
                                            {icon}
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            </DialogHeader>

                            {/* Modal Body */}
                            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
                                <>
                                    {/* ── KYC Tab ──────────────────────────────────────────────── */}
                                        {docTab === 'kyc' && (
                                            <div className="space-y-6">
                                                {/* KYC Info */}
                                                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                                                    <p className="text-xs font-bold uppercase text-slate-400 tracking-wider mb-3">Enfòmasyon Idantite</p>
                                                    <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                                                        <div>
                                                            <p className="text-slate-400 text-xs">Non konplè</p>
                                                            <p className="font-semibold text-slate-800 mt-0.5">{selected.applicant?.fullName ?? '—'}</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-slate-400 text-xs">Estati KYC</p>
                                                            <p className="font-semibold text-slate-800 mt-0.5 capitalize">{selected.applicant?.kycStatus ?? '—'}</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-slate-400 text-xs">Nimewo dokiman</p>
                                                            <p className="font-mono font-semibold text-slate-800 mt-0.5">{selected.applicant?.kyc?.kycDocNo ?? '—'}</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-slate-400 text-xs">Tip dokiman</p>
                                                            <p className="font-semibold text-slate-800 mt-0.5">{selected.applicant?.kyc?.kycDocType ?? '—'}</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-slate-400 text-xs">Dat nesans</p>
                                                            <p className="font-semibold text-slate-800 mt-0.5">{selected.applicant?.kyc?.kycBirthday ?? '—'}</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-slate-400 text-xs">Nasyonalite</p>
                                                            <p className="font-semibold text-slate-800 mt-0.5 uppercase">{selected.applicant?.kyc?.kycNationality ?? '—'}</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-slate-400 text-xs">Peyi</p>
                                                            <p className="font-semibold text-slate-800 mt-0.5 uppercase">{selected.applicant?.kyc?.kycCountry ?? '—'}</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-slate-400 text-xs">Ekspire</p>
                                                            <p className="font-semibold text-slate-800 mt-0.5">{selected.applicant?.kyc?.kycDocExpireDate ?? '—'}</p>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Document Images */}
                                                <div className="grid grid-cols-2 gap-3">
                                                    <DocImage label="Dokiman — Devan" r2Key={selected.applicant?.kyc?.frontDocUrl} />
                                                    <DocImage label="Dokiman — Dèyè" r2Key={selected.applicant?.kyc?.backDocUrl} />
                                                    <DocImage label="Selfie / Potret" r2Key={selected.applicant?.kyc?.portraitUrl} />
                                                    <DocImage label="Siyati" r2Key={selected.applicant?.kyc?.signatureUrl} />
                                                </div>

                                                {/* Contract PDF */}
                                                {selected.applicant?.kyc?.contractUrl && (
                                                    <div className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 bg-slate-50">
                                                        <div className="h-10 w-10 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
                                                            <FileText className="h-5 w-5 text-red-500" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm font-semibold text-slate-800">Kontrat PDF</p>
                                                            <p className="text-xs text-slate-400 truncate">{selected.applicant.kyc.contractUrl}</p>
                                                        </div>
                                                        <DocUrlButton r2Key={selected.applicant.kyc.contractUrl} />
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* ── Details Tab ──────────────────────────────────────────────── */}
                                        {docTab === 'details' && (
                                            <div className="space-y-5">
                                                {/* Contact */}
                                                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                                                    <p className="text-xs font-bold uppercase text-slate-400 tracking-wider mb-3 flex items-center gap-2">
                                                        <User className="h-3.5 w-3.5" /> Kontakt
                                                    </p>
                                                    <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                                                        <div>
                                                            <p className="text-slate-400 text-xs">Imèl</p>
                                                            <p className="font-semibold text-slate-800 mt-0.5">{selected.applicant?.email || '—'}</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-slate-400 text-xs">Telefòn</p>
                                                            <p className="font-semibold text-slate-800 mt-0.5">{selected.applicant?.phone || '—'}</p>
                                                        </div>
                                                        {(selected.applicant as any)?.kyc?.emergencyContact && (
                                                            <div className="col-span-2">
                                                                <p className="text-slate-400 text-xs">Kontakt ijans</p>
                                                                <p className="font-semibold text-slate-800 mt-0.5">{(selected.applicant as any).kyc.emergencyContact}</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Address */}
                                                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                                                    <p className="text-xs font-bold uppercase text-slate-400 tracking-wider mb-3 flex items-center gap-2">
                                                        <MapPin className="h-3.5 w-3.5" /> Adrès
                                                    </p>
                                                    <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                                                        <div className="col-span-2">
                                                            <p className="text-slate-400 text-xs">Adrès</p>
                                                            <p className="font-semibold text-slate-800 mt-0.5">{(selected as any).addressLine1 || '—'}</p>
                                                        </div>
                                                        {(selected as any).addressLine2 && (
                                                            <div className="col-span-2">
                                                                <p className="text-slate-400 text-xs">Katye / Depatman</p>
                                                                <p className="font-semibold text-slate-800 mt-0.5">{(selected as any).addressLine2}</p>
                                                            </div>
                                                        )}
                                                        <div>
                                                            <p className="text-slate-400 text-xs">Vil</p>
                                                            <p className="font-semibold text-slate-800 mt-0.5">{(selected as any).city || '—'}</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-slate-400 text-xs">Peyi</p>
                                                            <p className="font-semibold text-slate-800 mt-0.5 uppercase">{(selected as any).country || '—'}</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-slate-400 text-xs">Kòd postal</p>
                                                            <p className="font-semibold text-slate-800 mt-0.5">{(selected as any).postalCode || '—'}</p>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Payment & Fees */}
                                                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                                                    <p className="text-xs font-bold uppercase text-slate-400 tracking-wider mb-3 flex items-center gap-2">
                                                        <DollarSign className="h-3.5 w-3.5" /> Peman & Frè
                                                    </p>
                                                    <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                                                        <div>
                                                            <p className="text-slate-400 text-xs">Montan peye (lokal)</p>
                                                            <p className="font-bold text-slate-800 mt-0.5">
                                                                {Number(selected.amountPaidOriginal).toLocaleString()} {selected.paymentCurrency}
                                                            </p>
                                                        </div>
                                                        <div>
                                                            <p className="text-slate-400 text-xs">Ekivalan USD total</p>
                                                            <p className="font-bold text-slate-800 mt-0.5">{formatUSD(Number(selected.amountPaidUsdt))}</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-slate-400 text-xs">1ᵉʳ rechaj kat</p>
                                                            <p className="font-semibold text-slate-800 mt-0.5">{formatUSD(Number(selected.firstRechargeUsdt))}</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-slate-400 text-xs">Taux de change</p>
                                                            <p className="font-semibold text-slate-800 mt-0.5">
                                                                {selected.fxRateApplied ? `1 USD = ${Number(selected.fxRateApplied).toFixed(2)} HTG` : '—'}
                                                            </p>
                                                        </div>
                                                        <div>
                                                            <p className="text-slate-400 text-xs">Frè Bousanm</p>
                                                            <p className="font-semibold text-slate-800 mt-0.5">{formatUSD(Number((selected as any).bousanmFeeUsdt))}</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-slate-400 text-xs">Frè HyperPay</p>
                                                            <p className="font-semibold text-slate-800 mt-0.5">{formatUSD(Number((selected as any).hyperpayFeeUsdt))}</p>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Provider Info */}
                                                {((selected as any).providerApplicationId || (selected as any).providerCardId) && (
                                                    <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                                                        <p className="text-xs font-bold uppercase text-slate-400 tracking-wider mb-3 flex items-center gap-2">
                                                            <CreditCard className="h-3.5 w-3.5" /> Enfòmasyon Founisè
                                                        </p>
                                                        <div className="grid grid-cols-1 gap-y-3 text-sm">
                                                            {(selected as any).providerApplicationId && (
                                                                <div>
                                                                    <p className="text-slate-400 text-xs">ID Aplikasyon HyperPay</p>
                                                                    <p className="font-mono text-xs font-semibold text-slate-800 mt-0.5 break-all">{(selected as any).providerApplicationId}</p>
                                                                </div>
                                                            )}
                                                            {(selected as any).providerCardId && (
                                                                <div>
                                                                    <p className="text-slate-400 text-xs">ID Kat HyperPay</p>
                                                                    <p className="font-mono text-xs font-semibold text-slate-800 mt-0.5 break-all">{(selected as any).providerCardId}</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Tracking Timeline */}
                                                {(selected as any).trackingEvents?.length > 0 && (
                                                    <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                                                        <p className="text-xs font-bold uppercase text-slate-400 tracking-wider mb-3 flex items-center gap-2">
                                                            <History className="h-3.5 w-3.5" /> Istwa Estati
                                                        </p>
                                                        <div className="relative space-y-3 pl-4">
                                                            <div className="absolute left-1.5 top-1.5 bottom-1.5 w-px bg-slate-200" />
                                                            {((selected as any).trackingEvents as Array<{status: string; date: string; note?: string}>).map((ev, i) => (
                                                                <div key={i} className="relative flex items-start gap-3">
                                                                    <div className="absolute -left-4 mt-1 h-2.5 w-2.5 rounded-full bg-primary-500 border-2 border-white shadow" />
                                                                    <div className="pl-2">
                                                                        <p className="text-sm font-semibold text-slate-800">{STATUS_LABEL[ev.status] ?? ev.status}</p>
                                                                        {ev.note && <p className="text-xs text-slate-500 mt-0.5">{ev.note}</p>}
                                                                        <p className="text-xs text-slate-400 mt-0.5 font-mono">{formatDate(ev.date)}</p>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Review info */}
                                                {(selected.reviewNote || selected.rejectionReason || selected.reviewedBy) && (
                                                    <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
                                                        <p className="text-xs font-bold uppercase text-amber-500 tracking-wider mb-3 flex items-center gap-2">
                                                            <AlertCircle className="h-3.5 w-3.5" /> Nòt Revizyon
                                                        </p>
                                                        <div className="space-y-2 text-sm">
                                                            {selected.rejectionReason && (
                                                                <p className="text-red-700">
                                                                    <span className="font-semibold">Rezon refize:</span> {selected.rejectionReason}
                                                                </p>
                                                            )}
                                                            {selected.reviewNote && (
                                                                <p className="text-amber-800">
                                                                    <span className="font-semibold">Nòt:</span> {selected.reviewNote}
                                                                </p>
                                                            )}
                                                            {selected.reviewedBy && (
                                                                <p className="text-slate-500 text-xs">
                                                                    Pa: <span className="font-semibold">{selected.reviewedBy}</span>
                                                                    {selected.reviewedAt && ` — ${formatDate(selected.reviewedAt)}`}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Timestamps */}
                                                <div className="grid grid-cols-2 gap-3 text-xs text-slate-400">
                                                    <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                                                        <p className="font-semibold uppercase tracking-wider">Kreye</p>
                                                        <p className="font-mono mt-1">{formatDate(selected.createdAt)}</p>
                                                    </div>
                                                    <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                                                        <p className="font-semibold uppercase tracking-wider">Soumèt</p>
                                                        <p className="font-mono mt-1">{formatDate(selected.submittedAt)}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* ── Transactions Tab ──────────────────────────────────────── */}
                                        {docTab === 'transactions' && (
                                            <div className="space-y-4">
                                                {/* Volume summary */}
                                                {transactions.length > 0 && (
                                                    <div className="grid grid-cols-3 gap-3">
                                                        <div className="bg-slate-50 rounded-xl p-3 border border-slate-100 text-center">
                                                            <p className="text-xs text-slate-400 uppercase tracking-wider">Nòm tx</p>
                                                            <p className="text-2xl font-bold text-slate-800 mt-1">{transactions.length}</p>
                                                        </div>
                                                        <div className="bg-green-50 rounded-xl p-3 border border-green-100 text-center">
                                                            <p className="text-xs text-green-500 uppercase tracking-wider">Total lokal</p>
                                                            <p className="text-lg font-bold text-green-800 mt-1">
                                                                {Number(selected.amountPaidOriginal || 0).toLocaleString()} {selected.paymentCurrency}
                                                            </p>
                                                        </div>
                                                        <div className="bg-blue-50 rounded-xl p-3 border border-blue-100 text-center">
                                                            <p className="text-xs text-blue-400 uppercase tracking-wider">Total USD</p>
                                                            <p className="text-lg font-bold text-blue-800 mt-1">
                                                                {formatUSD(Number(selected.amountPaidUsdt || 0))}
                                                            </p>
                                                        </div>
                                                    </div>
                                                )}

                                                {txLoading ? (
                                                    <div className="flex items-center justify-center py-12">
                                                        <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
                                                    </div>
                                                ) : transactions.length === 0 ? (
                                                    <div className="py-12 text-center">
                                                        <DollarSign className="h-10 w-10 text-slate-200 mx-auto" />
                                                        <p className="text-slate-400 mt-3 text-sm">Okenn tranzaksyon jwenn</p>
                                                    </div>
                                                ) : (
                                                    <div className="space-y-2">
                                                        {transactions.map((tx) => (
                                                            <div key={tx.id} className="rounded-xl border border-slate-100 bg-slate-50/50 p-4">
                                                                <div className="flex items-start justify-between gap-3">
                                                                    <div className="flex-1 min-w-0">
                                                                        <p className="text-sm font-semibold text-slate-800">
                                                                            {TX_TYPE_LABEL[tx.txType] ?? tx.txType}
                                                                        </p>
                                                                        {tx.description && (
                                                                            <p className="text-xs text-slate-400 mt-0.5">{tx.description}</p>
                                                                        )}
                                                                        <p className="text-xs text-slate-400 mt-1 font-mono">{formatDate(tx.createdAt)}</p>
                                                                    </div>
                                                                    <div className="text-right flex-shrink-0">
                                                                        <p className="text-sm font-bold text-slate-900">
                                                                            {Number(tx.amountOriginal).toLocaleString()} {tx.currency}
                                                                        </p>
                                                                        {tx.amountUsdt != null && (
                                                                            <p className="text-xs text-slate-400">{formatUSD(Number(tx.amountUsdt))}</p>
                                                                        )}
                                                                        <Badge
                                                                            variant="outline"
                                                                            className={cn('text-xs mt-1', TX_STATUS_COLOR[tx.status] ?? 'bg-slate-50 text-slate-500 border-slate-200')}
                                                                        >
                                                                            {tx.status}
                                                                        </Badge>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                </>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* ── Action Confirmation Dialog ────────────────────────────────────────── */}
            <Dialog
                open={!!selected && !!actionType}
                onOpenChange={(open) => {
                    if (!open) { setActionType(null); setActionNote(''); }
                }}
            >
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            {actionType === 'approve' && <><CheckCircle2 className="h-5 w-5 text-green-600" /> Apwouve Aplikasyon</>}
                            {actionType === 'reject' && <><XCircle className="h-5 w-5 text-red-600" /> Refize Aplikasyon</>}
                            {actionType === 'request_info' && <><MessageSquare className="h-5 w-5 text-amber-500" /> Mande Plis Enfòmasyon</>}
                        </DialogTitle>
                        <DialogDescription>
                            {selected && (
                                <>
                                    Aplikasyon de{' '}
                                    <span className="font-bold text-slate-900">{selected.applicant?.fullName ?? 'Kliyan'}</span>
                                    {' '}— <TierBadge tier={selected.cardTierSlug} />
                                </>
                            )}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-2">
                        {actionType === 'approve' && (
                            <div className="flex items-start gap-3 p-3 rounded-xl bg-green-50 border border-green-100">
                                <Send className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                                <p className="text-sm text-green-800">
                                    Aplikasyon an ap voye dirèkteman bay <strong>HyperPay</strong> pou kreye kat la. Aksyon sa pa ka anile.
                                </p>
                            </div>
                        )}
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                                <MessageSquare className="h-4 w-4 text-slate-400" />
                                {actionType === 'approve' ? 'Nòt opsyonèl' : 'Rezon / Nòt *'}
                            </label>
                            <Textarea
                                placeholder={
                                    actionType === 'approve'
                                        ? 'Ajoute yon nòt si nesesè...'
                                        : actionType === 'reject'
                                        ? 'Eksplike rezon refize a (obligatwa)...'
                                        : 'Detaye enfòmasyon ou bezwen kliyan an voye...'
                                }
                                className="resize-none min-h-[100px] border-slate-200 focus:ring-primary-500"
                                value={actionNote}
                                onChange={(e) => setActionNote(e.target.value)}
                            />
                            {(actionType === 'reject' || actionType === 'request_info') && !actionNote.trim() && (
                                <p className="text-xs text-red-500 flex items-center gap-1">
                                    <AlertCircle className="h-3 w-3" /> Chan sa obligatwa
                                </p>
                            )}
                        </div>
                    </div>

                    <DialogFooter className="gap-2 sm:gap-0">
                        <Button
                            variant="outline"
                            className="border-slate-200"
                            onClick={() => { setActionType(null); setActionNote(''); }}
                        >
                            Anile
                        </Button>
                        <Button
                            className={cn(
                                'gap-2 shadow-md',
                                actionType === 'approve' ? 'bg-green-600 hover:bg-green-700' :
                                actionType === 'reject' ? 'bg-red-600 hover:bg-red-700' :
                                'bg-amber-500 hover:bg-amber-600'
                            )}
                            onClick={handleAction}
                            disabled={actionLoading || ((actionType === 'reject' || actionType === 'request_info') && !actionNote.trim())}
                        >
                            {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                                actionType === 'approve' ? <CheckCircle2 className="h-4 w-4" /> :
                                actionType === 'reject' ? <XCircle className="h-4 w-4" /> :
                                <Send className="h-4 w-4" />
                            )}
                            {actionType === 'approve' ? 'Konfime & Voye bay HyperPay' :
                             actionType === 'reject' ? 'Konfime Refize' :
                             'Voye Demand'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ── Admin Retry Confirmation Dialog ────────────────────────────────────── */}
            <Dialog
                open={!!retryAppId}
                onOpenChange={(open) => {
                    if (!open) { setRetryAppId(null); }
                }}
            >
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-slate-950 font-bold">
                            <RefreshCw className="h-5 w-5 text-orange-500 animate-spin-slow" />
                            Revoye Aplikasyon bay HyperPay
                        </DialogTitle>
                        <DialogDescription>
                            Aplikasyon sa a te rejte oswa li te echwe sou founisè a. Èske ou vle eseye voye li ankò?
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-2">
                        <div className="flex items-start gap-3 p-4 rounded-xl bg-orange-50 border border-orange-100">
                            <AlertCircle className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
                            <div className="text-sm text-orange-800 space-y-1">
                                <p className="font-bold text-orange-950">Nòt enpòtan:</p>
                                <p>Aplikasyon sa a pral voye dirèkteman bay HyperPay pou yo ka trete l ankò.</p>
                                <p className="font-semibold text-orange-700">⚠️ Lajan kliyan an PA ap debite ankò paske li te debite deja depi nan soumisyon.</p>
                            </div>
                        </div>
                    </div>

                    <DialogFooter className="gap-2 sm:gap-0">
                        <Button
                            variant="outline"
                            className="border-slate-200"
                            onClick={() => setRetryAppId(null)}
                        >
                            Anile
                        </Button>
                        <Button
                            className="bg-orange-500 hover:bg-orange-600 gap-2 shadow-md text-white border-none font-semibold"
                            onClick={async () => {
                                if (!retryAppId) return;
                                try {
                                    setActionLoading(true);
                                    const r = await cardAdminApi.retryProviderApplication(retryAppId);
                                    toast({ title: r.error ? 'Erè' : 'Siksè ✓', description: r.message, type: r.error ? 'error' : 'success' });
                                    setRetryAppId(null);
                                    setSelected(null);
                                    loadApplications();
                                } catch (e: any) {
                                    toast({ title: 'Erè', description: e?.response?.data?.message || e?.message || 'Erè', type: 'error' });
                                } finally {
                                    setActionLoading(false);
                                }
                            }}
                            disabled={actionLoading}
                        >
                            {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                            Wi, Voye Ankò
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// Small helper: fetch signed URL and open in new tab
function DocUrlButton({ r2Key }: { r2Key: string }) {
    const [loading, setLoading] = useState(false);
    const open = async () => {
        setLoading(true);
        try {
            const { url } = await cardAdminApi.getDocumentUrl(r2Key);
            window.open(url, '_blank');
        } finally {
            setLoading(false);
        }
    };
    return (
        <Button variant="outline" size="sm" className="gap-1.5 h-8 border-slate-200" onClick={open} disabled={loading}>
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ExternalLink className="h-3.5 w-3.5" />}
            Ouvri
        </Button>
    );
}
